import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Card } from 'react-bootstrap';

const EmailModal = ({
    show,
    handleClose,
    invoiceData,
    handleSendEmail,
    isReminder = false
}) => {
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [emailCopy, setEmailCopy] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (invoiceData && show) {
            // Set default email from invoice data
            setEmail(invoiceData.email || '');

            // Set default subject based on whether this is a reminder or regular email
            const invoiceNumber = invoiceData.invoicenumber || '';
            if (isReminder) {
                setSubject(`Påminnelse: Faktura ${invoiceNumber}`);
                setEmailBody(generateReminderTemplate(invoiceData));
            } else {
                setSubject(`Faktura ${invoiceNumber}`);
                setEmailBody(generateEmailTemplate(invoiceData));
            }
        }
    }, [invoiceData, show, isReminder]);

    const generateEmailTemplate = (invoice) => {
        const dueDate = invoice.deadline ? new Date(invoice.deadline).toLocaleDateString('sv-SE') : 'N/A';
        const amount = invoice.totals?.grand_total ?
            new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(invoice.totals.grand_total) :
            'N/A';

        return `Hej ${invoice.customername || ''},

Här kommer faktura ${invoice.invoicenumber || ''}.

Fakturabelopp: ${amount}
Förfallodatum: ${dueDate}

Vänligen betala fakturan till vårt bankgiro senast på förfallodatumet.

Med vänliga hälsningar,
${invoice.company_name || 'Företaget'}`;
    };

    const generateReminderTemplate = (invoice) => {
        const dueDate = invoice.deadline ? new Date(invoice.deadline).toLocaleDateString('sv-SE') : 'N/A';
        const amount = invoice.totals?.grand_total ?
            new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(invoice.totals.grand_total) :
            'N/A';

        return `Hej ${invoice.customername || ''},

Vi vill påminna om vår obetalda faktura ${invoice.invoicenumber || ''} som förföll den ${dueDate}.

Fakturabelopp: ${amount}

Vänligen betala fakturan omgående till vårt bankgiro.

Med vänliga hälsningar,
${invoice.company_name || 'Företaget'}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await handleSendEmail({
                email,
                subject,
                body: emailBody,
                sendCopy: emailCopy,
                isReminder
            });

            handleClose();
        } catch (err) {
            setError('Ett fel uppstod vid sändning av e-post. Vänligen försök igen.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isReminder ? 'Skicka påminnelse' : 'Skicka faktura via e-post'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                <Card className="mb-3">
                    <Card.Header as="h6">Fakturainformation</Card.Header>
                    <Card.Body>
                        <div className="d-flex flex-wrap">
                            <div className="me-4 mb-2">
                                <small className="text-muted">Fakturanr.</small>
                                <div><strong>{invoiceData?.invoicenumber || '-'}</strong></div>
                            </div>
                            <div className="me-4 mb-2">
                                <small className="text-muted">Kund</small>
                                <div><strong>{invoiceData?.customername || '-'}</strong></div>
                            </div>
                            <div className="me-4 mb-2">
                                <small className="text-muted">Belopp</small>
                                <div>
                                    <strong>
                                        {invoiceData?.totals?.grand_total
                                            ? new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(invoiceData.totals.grand_total)
                                            : '-'}
                                    </strong>
                                </div>
                            </div>
                            <div className="mb-2">
                                <small className="text-muted">Förfallodatum</small>
                                <div>
                                    <strong>
                                        {invoiceData?.deadline
                                            ? new Date(invoiceData.deadline).toLocaleDateString('sv-SE')
                                            : '-'}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Mottagarens e-postadress</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="E-postadress"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Ämne</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ämne"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Meddelande</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={10}
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Skicka en kopia till mig"
                            checked={emailCopy}
                            onChange={(e) => setEmailCopy(e.target.checked)}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Skickar...
                        </>
                    ) : (
                        isReminder ? 'Skicka påminnelse' : 'Skicka faktura'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EmailModal;